import { LEGACY_FONT_SCALE } from "common/constants/pdfSettings";
import { FontId } from "common/constants/textFont";
import type { SheetInches, SheetPixels } from "common/types/nominalNumbersTypes";
import { numDiv, numMul, sheetInchesNew, sheetPixelsToInches } from "common/utils/nominalNumbers";

export const fonts = {
  planSetRegular: FontId.ArialNarrow,
  planSetBold: FontId.ArialNarrow_Bold,

  brochureRegular: FontId.Circular,
  brochureBold: FontId.Circular_Bold,
  brochureBook: FontId.Circular_Book,
  brochureMedium: FontId.Circular_Medium,
} as const;

const preciseArialScalingFactor = 1.3969986357435198;
const preciseCircularScalingFactor = 1.4104372355430184;

export function preciseToLegacyPlanFontSize(fontSize: SheetInches): SheetInches {
  return numDiv(numMul(fontSize, LEGACY_FONT_SCALE), preciseArialScalingFactor);
}

export function legacyToPrecisePlanFontSize(fontSize: SheetInches): SheetInches {
  return numDiv(numMul(fontSize, preciseArialScalingFactor), LEGACY_FONT_SCALE);
}

export const DEFAULT_SMALL_FONT_SIZE = (1 / 16) as SheetInches;
export const DEFAULT_REGULAR_FONT_SIZE = (3 / 32) as SheetInches; // NOTES (ALL CAPS), PARAGRAPHS
export const DEFAULT_MEDIUM_FONT_SIZE = (1 / 8) as SheetInches; // SECTION TAG NUMBERS
export const DEFAULT_MEDIUMLARGE_FONT_SIZE = (3 / 16) as SheetInches;
export const DEFAULT_LARGE_FONT_SIZE = (5 / 16) as SheetInches; //TITLES (BOLD)
export const DEFAULT_EXTRALARGE_FONT_SIZE = (3 / 8) as SheetInches; // SHEET NUMBERS

export const STANDARD_FONT_SIZE = preciseToLegacyPlanFontSize(sheetPixelsToInches(10 as SheetPixels));
export const DIM_FONT_SIZE = STANDARD_FONT_SIZE;
export const ROOM_LABEL_FONT_SIZE = STANDARD_FONT_SIZE;
export const ROOM_LABEL_FONT_SIZE_DECOMP = preciseToLegacyPlanFontSize(sheetPixelsToInches(5.5 as SheetPixels));
export const SLAB_LABEL_FONT_SIZE = STANDARD_FONT_SIZE;

export const CASEWORK_FONT_SIZE = preciseToLegacyPlanFontSize(sheetPixelsToInches(7 as SheetPixels));
export const TAG_FONT_SIZE = preciseToLegacyPlanFontSize(sheetPixelsToInches(8 as SheetPixels));
export const TAG_FONT_SIZE_FOR_11_X_17 = STANDARD_FONT_SIZE;

/**
 * Brochure font sizes are defined as conversions of
 * SheetPixels for ease of use
 */

function preciseToLegacyBrochureFontSize(fontSizePixels: SheetPixels): SheetInches {
  const fontSizeInches = sheetPixelsToInches(fontSizePixels);
  return sheetInchesNew((fontSizeInches * LEGACY_FONT_SCALE) / preciseCircularScalingFactor);
}

export const brochureFontSizes = {
  extraSmall: preciseToLegacyBrochureFontSize(6 as SheetPixels),
  smallest: preciseToLegacyBrochureFontSize(7 as SheetPixels),
  smaller: preciseToLegacyBrochureFontSize(8 as SheetPixels),
  small: preciseToLegacyBrochureFontSize(10 as SheetPixels),
  medium: preciseToLegacyBrochureFontSize(12 as SheetPixels),
  medLarge: preciseToLegacyBrochureFontSize(14 as SheetPixels),
  large: preciseToLegacyBrochureFontSize(18 as SheetPixels),
  larger: preciseToLegacyBrochureFontSize(20 as SheetPixels),
  largest: preciseToLegacyBrochureFontSize(22 as SheetPixels),
  extraLarge: preciseToLegacyBrochureFontSize(30 as SheetPixels),
};

export interface FontSizes {
  small: SheetInches;
  regular: SheetInches; // NOTES (ALL CAPS), PARAGRAPHS
  medium: SheetInches; // SECTION TAG NUMBERS
  mediumLarge: SheetInches;
  large: SheetInches; //TITLES (BOLD)
  extraLarge: SheetInches; // SHEET NUMBERS
}
